// 📄 pages/Login/Login.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  AuthError,
  User,
} from 'firebase/auth';
import { CheckCircle2, Circle, RefreshCw } from 'lucide-react';
import { auth } from '../../firebase';
import OneHubLogo from '../../components/ui/OneHubLogo.tsx';
import PasswordInput from '../../components/ui/PasswordInput.tsx';
import { sendPasswordReset } from '../../services/authService.ts';
import { isValidEmail, isPasswordValid, PASSWORD_REQUIREMENTS } from '../../utils/authValidation.ts';

type LoginMode = 'login' | 'register' | 'reset';

const RESET_COOLDOWN_SECONDS = 60;

/**
 * Traduce el error crudo de Firebase Auth (login/registro) a un mensaje
 * legible — antes se mostraba `err.message` tal cual ("Firebase: Error
 * (auth/invalid-credential).").
 *
 * ⚠️ Anti-enumeración en login (mismo criterio que handleResetSubmit más
 * abajo, a propósito consistente): nunca se distingue "el correo no
 * existe" de "la contraseña está mal" — Firebase ya fusiona
 * auth/user-not-found y auth/wrong-password en auth/invalid-credential
 * para evitar que el login sirva para averiguar qué correos están
 * registrados; acá se respeta esa misma protección aunque alguna vez
 * llegue un código viejo/distinto (SDK previo a la fusión).
 */
const mensajeErrorAuth = (err: AuthError, mode: LoginMode): string => {
  const code = err?.code;

  if (code === 'auth/network-request-failed') return 'Sin conexión. Verifica tu internet e intenta de nuevo.';
  if (code === 'auth/too-many-requests') return 'Demasiados intentos. Espera unos minutos e intenta de nuevo.';
  if (code === 'auth/invalid-email') return 'Formato de correo inválido.';

  if (mode === 'register') {
    if (code === 'auth/email-already-in-use') return 'Ya existe una cuenta con este correo.';
    if (code === 'auth/weak-password') return 'La contraseña es demasiado débil.';
    return 'No se pudo crear la cuenta. Inténtalo de nuevo.';
  }

  // mode === 'login': auth/invalid-credential, auth/user-not-found,
  // auth/wrong-password y auth/user-disabled caen todos acá a propósito.
  return 'Correo o contraseña incorrectos.';
};

interface LoginProps {
  onLogin: (user: User) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState<LoginMode>('login');
  const [submitting, setSubmitting] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);

  const emailValid = isValidEmail(email);
  const showEmailError = (emailTouched || submitAttempted) && !emailValid;
  // Tras enviar el correo de reset (real o "simulado" por anti-enumeración),
  // se bloquea el input para que el usuario recuerde a qué correo lo pidió.
  const resetEmailLocked = mode === 'reset' && !!message;

  const canSubmit =
    mode === 'register'
      ? emailValid && isPasswordValid(password)
      : mode === 'reset'
      ? emailValid && resetCooldown === 0
      : emailValid && password.length > 0; // login: no se le exigen los nuevos requisitos a cuentas ya existentes

  // Cuenta regresiva del cooldown de "Reenviar correo": se reprograma un
  // segundo a la vez y se limpia sola al desmontar o al llegar a 0.
  useEffect(() => {
    if (resetCooldown === 0) return;
    const id = setTimeout(() => setResetCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [resetCooldown]);

  const switchMode = (nextMode: LoginMode) => {
    setMode(nextMode);
    setError('');
    setMessage('');
    setSubmitAttempted(false);
  };

  const handleResetSubmit = async () => {
    try {
      await sendPasswordReset(email);
      setMessage('¡Correo de restablecimiento enviado!');
      setResetCooldown(RESET_COOLDOWN_SECONDS);
    } catch (err) {
      const code = (err as AuthError)?.code;
      // ⚠️ Anti-enumeración: nunca revelar si el correo existe o no. Solo se
      // muestra un error real para fallos genuinos (red, cuota); cualquier
      // otro caso —incluido auth/user-not-found— resuelve como éxito.
      if (code === 'auth/network-request-failed') {
        setError('Sin conexión. Verifica tu internet e intenta de nuevo.');
      } else if (code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Espera unos minutos e intenta de nuevo.');
        setResetCooldown(RESET_COOLDOWN_SECONDS);
      } else if (code === 'auth/invalid-email') {
        setError('Formato de correo inválido.');
      } else {
        setMessage('¡Correo de restablecimiento enviado!');
        setResetCooldown(RESET_COOLDOWN_SECONDS);
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSubmitAttempted(true);
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      if (mode === 'register') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        onLogin(userCredential.user);
      } else if (mode === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        onLogin(userCredential.user);
      } else if (mode === 'reset') {
        await handleResetSubmit();
      }
    } catch (err) {
      setError(mensajeErrorAuth(err as AuthError, mode));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Nota: este form ya vive dentro del modal centrado que arma OperativeApp
    // (App.js), así que no necesita su propio wrapper de pantalla completa.
    <form onSubmit={handleSubmit} className="bg-gray-800 p-8 rounded-xl shadow-md w-full max-w-sm">
      <div className="flex justify-center mb-4">
        <OneHubLogo size={48} />
      </div>
      <h1 className="text-2xl font-bold mb-6 text-center text-blue-400">
        {mode === 'login' && 'One Hub'}
        {mode === 'register' && 'Crear Cuenta'}
        {mode === 'reset' && 'Restablecer Contraseña'}
      </h1>

      <input
        className="w-full p-2 rounded bg-gray-700 text-white disabled:opacity-60"
        type="email"
        placeholder="Correo electrónico"
        aria-label="Correo electrónico"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setEmailTouched(true)}
        disabled={resetEmailLocked}
      />
      {showEmailError && (
        <p className="text-red-400 text-xs mt-1">Ingresa un correo electrónico válido.</p>
      )}

      {(mode === 'login' || mode === 'register') && (
        <div className="mt-4">
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
          />
        </div>
      )}

      {mode === 'register' && (
        <ul className="mt-2 space-y-1">
          {PASSWORD_REQUIREMENTS.map((req) => {
            const met = req.test(password);
            return (
              <li
                key={req.id}
                className={`flex items-center gap-2 text-xs ${met ? 'text-green-400' : 'text-gray-400'}`}
              >
                {met ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                {req.label}
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="text-red-400 text-sm mt-4 mb-2">{error}</p>}
      {message && <p className="text-green-400 text-sm mt-4 mb-2">{message}</p>}

      <button
        type="submit"
        disabled={submitting || !canSubmit}
        className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded mt-4 mb-2 flex items-center justify-center gap-2"
      >
        {submitting ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : (
          <>
            {mode === 'login' && 'Iniciar Sesión'}
            {mode === 'register' && 'Registrarse'}
            {mode === 'reset' && (resetCooldown > 0 ? `Reenviar en ${resetCooldown}s...` : 'Reenviar correo')}
          </>
        )}
      </button>

      {mode === 'login' && (
        <>
          <p
            onClick={() => switchMode('reset')}
            className="text-sm text-blue-400 hover:underline text-center cursor-pointer mb-2"
          >
            ¿Olvidaste tu contraseña?
          </p>
          <p className="text-sm text-gray-400 text-center">
            ¿No tienes una cuenta?{' '}
            <span
              className="text-blue-400 hover:underline cursor-pointer"
              onClick={() => switchMode('register')}
            >
              Regístrate aquí
            </span>
          </p>
        </>
      )}

      {mode === 'register' && (
        <p className="text-sm text-gray-400 text-center">
          ¿Ya tienes una cuenta?{' '}
          <span
            className="text-blue-400 hover:underline cursor-pointer"
            onClick={() => switchMode('login')}
          >
            Iniciar Sesión
          </span>
        </p>
      )}

      {mode === 'reset' && (
        <p className="text-sm text-gray-400 text-center">
          Volver a{' '}
          <span
            className="text-blue-400 hover:underline cursor-pointer"
            onClick={() => switchMode('login')}
          >
            Iniciar Sesión
          </span>
        </p>
      )}

      <p className="text-sm text-gray-400 mt-4 text-center">
        ¿Necesitas ayuda?{' '}
        <a href="mailto:support@OneHub.com" className="text-blue-400 hover:underline">
          Contáctanos
        </a>
      </p>
    </form>
  );
}
